<template>
  <div>
    <div class="grid min-w-0 w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1">
      <UButton
        color="neutral"
        variant="subtle"
        size="sm"
        class="shrink-0"
        icon="i-lucide-chevron-left"
        :aria-label="$t('order.filters.previousDateRange')"
        @click="move(-1)"
      />

      <div data-testid="order-date-range-fields" class="flex min-w-0 flex-col gap-1">
        <date-field
          v-model="startValue"
          :status="status"
          :augmenter="augmenters.YMD"
          :max-date="startMaxDate"
          size="sm"
          class="min-w-0 w-full"
        />

        <span class="sr-only">–</span>

        <date-field
          v-model="finishValue"
          :status="status"
          :augmenter="augmenters.YMD"
          :min-date="finishMinDate"
          size="sm"
          class="min-w-0 w-full"
        />
      </div>

      <UButton
        color="neutral"
        variant="subtle"
        size="sm"
        class="shrink-0"
        icon="i-lucide-chevron-right"
        :aria-label="$t('order.filters.nextDateRange')"
        @click="move(1)"
      />
    </div>

    <div class="mt-3">
      <o-chip
        v-for="option in units"
        :key="option.label"
        class="mb-1 mr-1 cursor-pointer"
        :variant="unit === option.value ? 'primary' : 'default'"
        :flat="unit === option.value"
        @click="unit = option.value"
      >
        {{ option.label }}
      </o-chip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UButton } from '#components'

import {
  DateField,
} from '@/components/fields'

import { augmenters } from '@/composables/useFieldAugmenters'

const { $date, $dateFns, $i18n } = useNuxtApp()

interface Props {
  modelValue: any
  status?: string | boolean | null
}

const props = withDefaults(defineProps<Props>(), {
  status: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
}>()

const unit = ref('weeks')

const _value = computed({
  get: () => props.modelValue,
  set: (val: any) => emit('update:modelValue', val),
})

const startValue = computed({
  get: () => props.modelValue.start,
  set: (value: any) => handleStartChange({ old: props.modelValue.start, new: value }),
})

const finishValue = computed({
  get: () => props.modelValue.finish,
  set: (value: any) => handleFinishChange({ old: props.modelValue.finish, new: value }),
})

const startMaxDate = computed(() => {
  return unit.value === 'custom' ? parseRangeDate(props.modelValue.finish) : null
})

const finishMinDate = computed(() => {
  return unit.value === 'custom' ? parseRangeDate(props.modelValue.start) : null
})

const units = computed(() => [
  { label: $i18n.t('datetime.range.daily'), value: 'days' },
  { label: $i18n.t('datetime.range.weekly'), value: 'weeks' },
  { label: $i18n.t('datetime.range.monthly'), value: 'months' },
  { label: $i18n.t('datetime.range.quarterly'), value: 'quarter' },
  { label: $i18n.t('datetime.range.yearly'), value: 'years' },
  { label: $i18n.t('general.custom'), value: 'custom' },
])

watch(unit, (newUnit) => {
  if (newUnit !== 'custom') {
    const start = $date.parse(_value.value.start)
    const date = $date.startOf(start, newUnit)
    set(date, newUnit, 0)
  }
})

function move(value: number) {
  const start = $date.parse(_value.value.start)
  const finish = $date.parse(_value.value.finish)
  const u = unit.value

  const _unit = u === 'custom' ? 'days' : u
  const _val = u === 'custom' ? (($dateFns.differenceInDays(finish, start) + 1) * value) : value

  set(start, _unit, _val)
}

function set(date: Date, u: string, value: number) {
  const _unit = u === 'quarter' ? 'months' : u
  const duration: Record<string, number> = {}

  duration[_unit] = value
  const start = $date.add(date, duration)

  let amount = value + (Math.abs(value) > 1 ? value - 1 : 1)

  if (u === 'quarter') {
    amount = amount * 3
  }

  duration[_unit] = amount
  duration.days = duration.days || -1

  const finish = unit.value === 'days' ? start : $date.add(date, duration)

  const formattedStart = $date.format(start)
  const formattedFinish = $date.format(new Date((Math.round($dateFns.endOfDay(finish).getTime() / 1000) - 1) * 1000))

  _value.value = {
    start: formattedStart,
    finish: formattedFinish,
  }
}

function parseRangeDate(value: any): Date | null {
  const date = $date.parse(value)
  return isNaN(date.getTime()) ? null : date
}

function handleStartChange(value: { old: any; new: any }) {
  const u = unit.value
  const date = parseRangeDate(value.new)

  if (date && u !== 'custom') {
    set(date, u, 0)
    return
  }

  _value.value = { ...props.modelValue, start: value.new }
}

function handleFinishChange(value: { old: any; new: any }) {
  _value.value = { ...props.modelValue, finish: value.new }

  if (value.old !== value.new) {
    unit.value = 'custom'
  }
}
</script>
